/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useCallback, useState } from 'react';
import config from '../../../config';
import { FormGenerator, FieldProps } from '@g17eco/molecules/form';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import G17Client from '../../../services/G17Client';
import { RegisterInterestData } from '../../../types/open';
import { isValidEmail } from '../../../utils';
import './RegisterInterestForm.scss';
import { BasicAlert } from '@g17eco/molecules/alert';

interface FormFields extends FieldProps {
  code: keyof RegisterInterestData;
}

const fields: FormFields[] = [
  {
    code: 'first_name',
    type: 'text',
    label: 'First name',
    placeholder: 'First name',
    required: true,
  },
  {
    code: 'last_name',
    type: 'text',
    label: 'Last name',
    placeholder: 'Last name',
    required: true,
  },
  {
    code: 'email',
    type: 'email',
    label: 'Email',
    placeholder: 'Email address',
    required: true,
  },
  {
    code: 'title',
    type: 'text',
    label: 'Job title',
    placeholder: 'Job title',
  },
  {
    code: 'company',
    type: 'text',
    label: 'Company',
    placeholder: 'Company',
  },
  {
    code: 'whereDidYouHear',
    type: 'text',
    label: 'Where did you hear about us?',
    placeholder: 'Where did you hear about us?',
  },
  {
    code: 'message',
    type: 'textarea',
    label: 'Message',
    placeholder: 'Type here',
    required: true,
  },
];

export interface RegisterInterestFormProps {
  interestedIn?: string;
  productCode?: string;
  title?: string;
  onSubmit: (data: unknown) => unknown;
}

const getInitialState = () => ({
  isLoading: false,
  isCompleted: false,
  error: false,
  errorMessage: '',
});
type SubmitState = ReturnType<typeof getInitialState>;

export const RegisterInterestForm = (props: RegisterInterestFormProps) => {
  const [submitState, setSubmitState] = useState<SubmitState>(getInitialState());

  const [formData, setFormData] = useState<RegisterInterestData>({
    oid: config.crm.formId,
    title: '',
    salutation: '',
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    whereDidYouHear: '',
    message: '',
    interestedIn: props.interestedIn,
    productCode: props.productCode,
    siteSource: window.location.href,
  });

  const onChange = useCallback(
    (e: React.ChangeEvent<any>) => {
      const { name, value } = e.target;
      setFormData((form) => ({ ...form, siteSource: window.location.href, [name]: value }));
    },
    [setFormData]
  );

  const validEmail = isValidEmail(formData.email);
  const missingFields = fields.some((f) => f.required && !formData[f.code]);
  const isFormValid = validEmail && !missingFields;

  const handleSubmit = () => {
    setSubmitState({ ...getInitialState(), isLoading: true });
    return G17Client.registerInterest(formData)
      .then((data) => {
        setSubmitState((s) => ({ ...s, isLoading: false, isCompleted: true }));
        props.onSubmit(data);
      })
      .catch((e) => {
        setSubmitState({
          ...getInitialState(),
          error: true,
          errorMessage: e.message,
        });
      });
  };

  return (
    <div className={'register-interest-form'}>
      {props.title !== '' && <h1 className='h1 h1--black'>{props.title ?? 'Contact us'}</h1>}
      <BasicAlert type={'danger'} hide={!submitState.error}>
        {submitState.errorMessage}
      </BasicAlert>

      <FormGenerator fields={fields} form={formData} updateForm={onChange} />

      <div className={'text-right'}>
        <SubmitButton className='px-5' disabled={!isFormValid} color='primary' onClick={handleSubmit}>
          Submit
        </SubmitButton>
      </div>
    </div>
  );
};
